import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import communityAPI from '../services/community.api.js';
import followService from '../services/follow.service';
import useAuthStore from './authStore';

const useCommunityStore = create(
  persist(
    (set, get) => ({
      // State
      feed: [],
      selectedPost: null,
      postComments: [],
      conversations: [],
      messages: [],
      selectedUser: null,
      searchResults: {
        users: [],
        posts: [],
      },
      userStats: null,
      followers: [],
      following: [],

      // Add unread message count to state
      unreadMessageCount: 0,

      // Pagination
      feedPagination: null,
      commentsPagination: null,
      conversationsPagination: null,
      messagesPagination: null,
      searchPagination: null,

      // UI State
      isLoading: false,
      error: null,
      activeTab: 'feed',
      selectedConversation: null,
      showCreatePostModal: false,
      showReportModal: false,

      // =================== POST ACTIONS ===================
      createPost: async (content, metadata = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await communityAPI.createPost({
            content,
            metadata
          });

          set(state => ({
            feed: [response.data, ...state.feed],
            showCreatePostModal: false,
          }));

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getFeed: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await communityAPI.getFeed(params);

          set({
            feed: response.data.posts,
            feedPagination: response.data.pagination,
          });

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getPost: async (postId) => {
        set({ isLoading: true, error: null });
        try {
          const response = await communityAPI.getPost(postId);

          set({ selectedPost: response.data });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      updatePost: async (postId, content, mediaUrl = null) => {
        set({ isLoading: true, error: null });
        try {
          const response = await communityAPI.updatePost(postId, { content, mediaUrl });

          // Update in feed
          set(state => ({
            feed: state.feed.map(post =>
              post.id === postId ? response.data : post
            ),
            selectedPost: response.data,
          }));

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      deletePost: async (postId) => {
        set({ isLoading: true, error: null });
        try {
          const response = await communityAPI.deletePost(postId);

          // Remove from feed
          set(state => ({
            feed: state.feed.filter(post => post.id !== postId),
            selectedPost: null,
          }));

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getUserPosts: async (userId, params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await communityAPI.getUserPosts(userId, params);
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // =================== COMMENT ACTIONS ===================
      createComment: async (postId, content) => {
        set({ isLoading: true, error: null });
        try {
          const response = await communityAPI.createComment(postId, { content });

          // Add to comments
          set(state => ({
            postComments: [response.data, ...state.postComments],
          }));

          // Update post comment count in feed
          set(state => ({
            feed: state.feed.map(post =>
              post.id === postId
                ? {
                    ...post,
                    _count: { ...post._count, comments: post._count.comments + 1 }
                  }
                : post
            ),
          }));

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getPostComments: async (postId, params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await communityAPI.getPostComments(postId, params);

          set({
            postComments: response.data.comments,
            commentsPagination: response.data.pagination,
          });

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      updateComment: async (commentId, content) => {
        set({ isLoading: true, error: null });
        try {
          const response = await communityAPI.updateComment(commentId, { content });

          // Update in comments
          set(state => ({
            postComments: state.postComments.map(comment =>
              comment.id === commentId ? response.data : comment
            ),
          }));

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      deleteComment: async (commentId) => {
        set({ isLoading: true, error: null });
        try {
          const response = await communityAPI.deleteComment(commentId);

          // Remove from comments
          set(state => ({
            postComments: state.postComments.filter(comment => comment.id !== commentId),
          }));

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // =================== LIKE ACTIONS ===================
      toggleLike: async (postId = null, commentId = null) => {
        set({ isLoading: true, error: null });
        try {
          const response = await communityAPI.toggleLike({ postId, commentId });

          if (postId) {
            // Update post likes
            set(state => ({
              feed: state.feed.map(post => {
                if (post.id === postId) {
                  const liked = response.data.liked;
                  return {
                    ...post,
                    likedByUser: liked,
                    _count: {
                      ...post._count,
                      likes: liked ? post._count.likes + 1 : post._count.likes - 1
                    },
                  };
                }
                return post;
              }),
            }));
          }

          if (commentId) {
            // Update comment likes
            set(state => ({
              postComments: state.postComments.map(comment => {
                if (comment.id === commentId) {
                  const liked = response.data.liked;
                  return {
                    ...comment,
                    likedByUser: liked,
                    _count: {
                      ...comment._count,
                      likes: liked ? (comment._count?.likes || 0) + 1 : (comment._count?.likes || 1) - 1
                    },
                  };
                }
                return comment;
              }),
            }));
          }

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // =================== FOLLOW ACTIONS ===================
      toggleFollow: async (followingId) => {
        set({ isLoading: true, error: null });
        try {
          const response = await followService.toggleFollow(followingId);

          // Update local state if needed
          return response;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getFollowers: async (userId, params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await followService.getFollowers(userId, true); // force refresh

          set({ followers: response.followers });
          return response;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getFollowing: async (userId, params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await followService.getFollowing(userId, true); // force refresh

          set({ following: response.following });
          return response;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      checkFollowStatus: async (userId) => {
        set({ isLoading: true, error: null });
        try {
          const response = await followService.checkFollowStatus('current', userId);
          return response;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          console.error('Check follow status error:', message);
          set({ error: message });
          return { following: false };
        } finally {
          set({ isLoading: false });
        }
      },

      getFollowSuggestions: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await followService.getFollowSuggestions();
          return response;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          console.error('Get follow suggestions error:', message);
          set({ error: message });
          return { suggestions: [] };
        } finally {
          set({ isLoading: false });
        }
      },

      getMutualFollows: async (userId) => {
        set({ isLoading: true, error: null });
        try {
          const response = await followService.getMutualFollows(userId);
          return response;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          console.error('Get mutual follows error:', message);
          set({ error: message });
          return { mutualFollows: [] };
        } finally {
          set({ isLoading: false });
        }
      },

      // =================== MESSAGE ACTIONS ===================
      sendMessage: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await communityAPI.sendMessage(data);

          // Update local state
          set(state => ({
            messages: [...state.messages, response.data],
          }));

          // Refresh unread count after sending
          await get().getUnreadMessageCount();

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getConversations: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await communityAPI.getConversations(params);

          set({
            conversations: response.data.conversations,
            conversationsPagination: response.data.pagination,
          });

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getMessages: async (userId, params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await communityAPI.getMessages(userId, params);

          set({
            messages: response.data.messages,
            messagesPagination: response.data.pagination,
            selectedConversation: userId,
          });

          // Refresh unread count after viewing messages
          await get().getUnreadMessageCount();

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getUnreadMessageCount: async () => {
        try {
          const response = await communityAPI.getUnreadMessageCount();
          const count = response?.data?.count ?? 0;
          set({ unreadMessageCount: count });
          return count;
        } catch (error) {
          console.error('Get unread message count error:', error);
          return 0;
        }
      },

      deleteMessage: async (messageId) => {
        set({ isLoading: true, error: null });
        try {
          const response = await communityAPI.deleteMessage(messageId);

          // Remove from messages
          set(state => ({
            messages: state.messages.filter(message => message.id !== messageId),
          }));

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      archiveConversation: async (conversationId) => {
        set({ isLoading: true, error: null });
        try {
          const response = await communityAPI.archiveConversation(conversationId);

          set(state => ({
            conversations: state.conversations.filter(conv => conv.id !== conversationId),
            selectedConversation: state.selectedConversation?.id === conversationId ? null : state.selectedConversation
          }));

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      blockConversation: async (conversationId) => {
        set({ isLoading: true, error: null });
        try {
          const response = await communityAPI.blockConversation(conversationId);

          set(state => ({
            conversations: state.conversations.filter(conv => conv.id !== conversationId),
            selectedConversation: state.selectedConversation?.id === conversationId ? null : state.selectedConversation
          }));

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // =================== SEARCH ACTIONS ===================
      searchUsers: async (query, params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await communityAPI.searchUsers({ query, ...params });

          set({
            searchResults: {
              ...get().searchResults,
              users: response.data.users,
            },
            searchPagination: response.data.pagination,
          });

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      searchPosts: async (query, params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await communityAPI.searchPosts({ query, ...params });

          set({
            searchResults: {
              ...get().searchResults,
              posts: response.data.posts,
            },
            searchPagination: response.data.pagination,
          });

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // =================== REPORT ACTIONS ===================
      createReport: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await communityAPI.createReport(data);

          set({ showReportModal: false });

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // =================== STATS ACTIONS ===================
      getUserStats: async (userId) => {
        set({ isLoading: true, error: null });
        try {
          const response = await communityAPI.getUserStats(userId);

          set({ userStats: response.data });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getMyStats: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await communityAPI.getMyStats();

          set({ userStats: response.data });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          console.error('Get my stats error:', message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // =================== UI ACTIONS ===================
      setActiveTab: (tab) => set({ activeTab: tab }),
      setSelectedConversation: (conversation) => set({ selectedConversation: conversation }),
      setShowCreatePostModal: (show) => set({ showCreatePostModal: show }),
      setShowReportModal: (show) => set({ showReportModal: show }),
      setSelectedUser: (user) => set({ selectedUser: user }),

      clearSearchResults: () => set({
        searchResults: { users: [], posts: [] },
        searchPagination: null,
      }),

      clearMessages: () => set({
        messages: [],
        selectedConversation: null,
        messagesPagination: null,
      }),

      clearComments: () => set({
        postComments: [],
        commentsPagination: null,
      }),

      clearError: () => set({ error: null }),

      // =================== REAL-TIME UPDATE METHODS ===================
      addRealTimePost: (post) => {
        set(state => ({
          feed: [post, ...state.feed]
        }));
      },

      updatePostLikes: (postId, userId, action) => {
        const authUser = useAuthStore.getState().user;
        set(state => ({
          feed: state.feed.map(post => {
            if (post.id === postId) {
              const currentLikes = post._count?.likes || 0;
              const likedByUser = action === 'liked' ?
                (post.likedByUser || userId === authUser?.id) :
                false;

              return {
                ...post,
                likedByUser,
                _count: {
                  ...post._count,
                  likes: action === 'liked' ? currentLikes + 1 : Math.max(0, currentLikes - 1)
                }
              };
            }
            return post;
          })
        }));
      },

      addRealTimeComment: (comment) => {
        set(state => ({
          postComments: [comment, ...state.postComments],
          feed: state.feed.map(post => {
            if (post.id === comment.postId) {
              return {
                ...post,
                _count: {
                  ...post._count,
                  comments: (post._count?.comments || 0) + 1
                }
              };
            }
            return post;
          })
        }));
      },

      addRealTimeMessage: (message) => {
        const { selectedConversation, unreadMessageCount } = get();
        const authUser = useAuthStore.getState().user;

        set(state => {
          // Add to messages if it's the current conversation
          const isCurrentConversation = selectedConversation?.user?.id === message.senderId ||
                                       selectedConversation?.user?.id === message.receiverId;

          const newMessages = isCurrentConversation
            ? [...state.messages, message]
            : state.messages;

          // Update conversations list
          const updatedConversations = state.conversations.map(conv => {
            if (conv.user.id === message.senderId || conv.user.id === message.receiverId) {
              return {
                ...conv,
                lastMessage: message.content?.substring(0, 200),
                lastMessageAt: message.createdAt,
                unreadCount: (message.senderId !== authUser?.id && !isCurrentConversation)
                  ? (conv.unreadCount || 0) + 1
                  : conv.unreadCount
              };
            }
            return conv;
          });

          // Update unread count
          const isUnread = message.senderId !== authUser?.id;
          const newUnreadCount = isUnread && !isCurrentConversation
            ? unreadMessageCount + 1
            : unreadMessageCount;

          return {
            messages: newMessages,
            conversations: updatedConversations,
            unreadMessageCount: newUnreadCount
          };
        });
      },

      markMessagesAsRead: (senderId) => {
        set(state => ({
          messages: state.messages.map(msg =>
            msg.senderId === senderId && !msg.read
              ? { ...msg, read: true, readAt: new Date().toISOString() }
              : msg
          ),
          conversations: state.conversations.map(conv =>
            conv.user.id === senderId
              ? { ...conv, unreadCount: 0 }
              : conv
          ),
          unreadMessageCount: Math.max(0, state.unreadMessageCount -
            state.messages.filter(msg => msg.senderId === senderId && !msg.read).length
          )
        }));
      },

      removeMessage: (messageId) => {
        set(state => ({
          messages: state.messages.filter(msg => msg.id !== messageId)
        }));
      },

      updateFollowStatus: (followerId, followingId, isFollowing) => {
        const authUser = useAuthStore.getState().user;
        set(state => {
          // Update user stats if it's the current user
          if (followingId === authUser?.id) {
            return {
              userStats: {
                ...state.userStats,
                followersCount: isFollowing ?
                  (state.userStats?.followersCount || 0) + 1 :
                  Math.max(0, (state.userStats?.followersCount || 1) - 1)
              }
            };
          }
          return state;
        });
      },

      removePost: (postId) => {
        set(state => ({
          feed: state.feed.filter(post => post.id !== postId),
          selectedPost: state.selectedPost?.id === postId ? null : state.selectedPost
        }));
      },

      updatePostRealTime: (updatedPost) => {
        set(state => ({
          feed: state.feed.map(post =>
            post.id === updatedPost.id ? updatedPost : post
          ),
          selectedPost: state.selectedPost?.id === updatedPost.id ? updatedPost : state.selectedPost
        }));
      }
    }),
    {
      name: 'community-storage',
      partialize: (state) => ({
        conversations: state.conversations,
        selectedConversation: state.selectedConversation,
      }),
    }
  )
);

export default useCommunityStore;